import type { CustomerStatus } from "../../types/customer.type";

export const CUSTOMER_LIST_PATH = "/customer";

/** The route a customer row links to. `uuid` is what the details page reads. */
export const customerDetailsPath = (uuid: string) =>
  `${CUSTOMER_LIST_PATH}/details/${uuid}`;

/** Query param the list's branch filter starts from, so another screen can open one branch's shops. */
export const CUSTOMER_BRANCH_PARAM = "branch_uuid";

/** The customer list filtered to one branch (honoured for the roles that may pick a branch). */
export const customerListPathForBranch = (branchUuid: string) =>
  `${CUSTOMER_LIST_PATH}?${new URLSearchParams({
    [CUSTOMER_BRANCH_PARAM]: branchUuid,
  }).toString()}`;

// "พักใช้งาน" is its own filter value, not a flavour of "กำลังใช้งาน": picking กำลังใช้งาน
// asks the backend for status=enable and returns only those rows. The point of the status is
// to be able to separate the two, so folding them together here would defeat it.
export const STATUS_FILTER = [
  { label: "ทั้งหมด", value: "all" },
  { label: "กำลังใช้งาน", value: "enable" },
  { label: "พักใช้งาน", value: "suspend" },
  { label: "ไม่เปิดใช้งาน", value: "disable" },
];

/**
 * How each customer status reads on screen. Centralised because three views render it
 * (the list Chip and the two details panels) and every one of them used to be an
 * `=== "enable" ? ... : ...` ternary — which silently labels a third value as
 * "ไม่เปิดใช้งาน", the one thing พักใช้งาน is not.
 *
 * Amber for พักใช้งาน: green reads as "selling", red as "closed", and this is neither.
 *
 * `color` doubles as the MUI Chip colour and the `theme.palette[color].main` behind the
 * status dot on the details panels, so the two can never drift apart.
 */
export const CUSTOMER_STATUS_DISPLAY: Record<
  CustomerStatus,
  { label: string; color: "success" | "warning" | "error" }
> = {
  enable: { label: "กำลังใช้งาน", color: "success" },
  suspend: { label: "พักใช้งาน", color: "warning" },
  disable: { label: "ไม่เปิดใช้งาน", color: "error" },
};

/**
 * Anything unrecognised falls back to ไม่เปิดใช้งาน — most notably the NULL that the bulk of
 * synced customer rows carry. That is not a new decision: it is exactly what the
 * `status === "enable" ? ... : ...` ternaries this helper replaces already displayed, and
 * changing it is a separate call from adding พักใช้งาน.
 */
export const customerStatusDisplay = (status: CustomerStatus | null | undefined) =>
  (status && CUSTOMER_STATUS_DISPLAY[status]) || CUSTOMER_STATUS_DISPLAY.disable;

// "เงินสด,เครดิต" is gone: it was never a way of billing, only a deferred decision, and the
// decision has nowhere to be made — cash sale or invoice is fixed when the document is issued.
// The edit form has only ever offered ขายสด / ขายเชื่อ; this filter now agrees with it.
export const BILL_TYPE_FILTER = [
  { label: "ทั้งหมด", value: "all" },
  { label: "เงินสด", value: "cash" },
  { label: "เครดิต", value: "credit" },
];

// Which tax invoice the shop gets — accounting's `etax_enabled` flag on the customer. Same
// ETAX / PAPER values the E-Tax document list filters bills by.
export const ETAX_CHANNEL_FILTER = [
  { label: "ทั้งหมด", value: "all" },
  { label: "ใบกำกับภาษีอิเล็กทรอนิกส์", value: "ETAX" },
  { label: "ใบกำกับภาษีแบบกระดาษ", value: "PAPER" },
];
